import {
  Contact,
  ContactsResponse,
  ContactResponse,
  ContactPagination,
  ContactSummary,
  CreateContactRequest,
  UpdateContactRequest,
  ContactStatus
} from '../types/contact'

// API URL - using CRO Service
const getApiUrl = (): string => {
  const envUrl = import.meta.env.VITE_CRO_API_URL
  const mode = import.meta.env.MODE

  if (envUrl) {
    return envUrl
  }

  const API_URLS = {
    local: 'http://localhost:3007/api/v1',
    remote: 'https://ath-avi-cro-uat-west.azurewebsites.net/api/v1'
  }

  return mode === 'remote' ? API_URLS.remote : API_URLS.local
}

class ContactService {
  private baseURL: string

  constructor() {
    this.baseURL = getApiUrl()
  }

  private getAuthToken(): string | null {
    return localStorage.getItem('auth-token')
  }

  private getHeaders(): Record<string, string> {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json'
    }

    const token = this.getAuthToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }

    return headers
  }

  private async handleResponse<T>(response: Response): Promise<T> {
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}))
      throw new Error(
        errorData.error?.message || errorData.message || `HTTP ${response.status}`
      )
    }
    return response.json()
  }

  /**
   * Get all contacts with optional filtering and pagination
   * @param customerId - Customer ID (required)
   * @param search - Optional search query (searches first_name, last_name, email)
   * @param partnerId - Optional filter by partner ID
   * @param status - Optional filter by status
   * @param roleId - Optional filter by role ID
   * @param isPrimary - Optional filter by primary status
   * @param page - Page number (default: 1)
   * @param limit - Items per page (default: 10, max: 100)
   */
  async getContacts(
    customerId: string,
    search?: string,
    partnerId?: string,
    status?: ContactStatus,
    roleId?: string,
    isPrimary?: boolean,
    page?: number,
    limit?: number
  ): Promise<{ data: Contact[]; summary: ContactSummary; pagination: ContactPagination }> {
    const url = new URL(`${this.baseURL}/contacts`)

    // Required parameter
    url.searchParams.append('customerId', customerId)

    if (search !== undefined && search.trim()) {
      url.searchParams.append('search', search.trim())
    }
    if (partnerId !== undefined) {
      url.searchParams.append('partner_id', partnerId)
    }
    if (status !== undefined) {
      url.searchParams.append('status', status)
    }
    if (roleId !== undefined) {
      url.searchParams.append('role_id', roleId)
    }
    if (isPrimary !== undefined) {
      url.searchParams.append('is_primary', String(isPrimary))
    }
    if (page !== undefined) {
      url.searchParams.append('page', String(page))
    }
    if (limit !== undefined) {
      url.searchParams.append('limit', String(limit))
    }

    const timeoutMs = 30000 // 30 second timeout

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    // Create timeout promise
    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const responseData = await this.handleResponse<ContactsResponse>(response)

    return {
      data: responseData.data,
      summary: responseData.summary,
      pagination: responseData.pagination
    }
  }

  /**
   * Get a single contact by ID
   * @param id - Contact UUID
   * @param customerId - Customer ID (required)
   */
  async getContact(id: string, customerId: string): Promise<Contact> {
    const url = new URL(`${this.baseURL}/contacts/${id}`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'GET',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<ContactResponse>(response)

    return data.data
  }

  /**
   * Create a new contact
   * @param customerId - Customer ID (required)
   * @param contactData - Contact data
   */
  async createContact(customerId: string, contactData: CreateContactRequest): Promise<Contact> {
    const url = new URL(`${this.baseURL}/contacts`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'POST',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(contactData)
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<ContactResponse>(response)

    return data.data
  }

  /**
   * Update an existing contact
   * @param id - Contact UUID
   * @param customerId - Customer ID (required)
   * @param updates - Partial contact data to update
   */
  async updateContact(id: string, customerId: string, updates: UpdateContactRequest): Promise<Contact> {
    const url = new URL(`${this.baseURL}/contacts/${id}`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'PUT',
      headers: this.getHeaders(),
      credentials: 'include',
      body: JSON.stringify(updates)
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    const data = await this.handleResponse<ContactResponse>(response)

    return data.data
  }

  /**
   * Delete a contact (soft delete)
   * @param id - Contact UUID
   * @param customerId - Customer ID (required)
   */
  async deleteContact(id: string, customerId: string): Promise<void> {
    const url = new URL(`${this.baseURL}/contacts/${id}`)
    url.searchParams.append('customerId', customerId)

    const timeoutMs = 30000

    const config: RequestInit = {
      method: 'DELETE',
      headers: this.getHeaders(),
      credentials: 'include'
    }

    const timeoutPromise = new Promise<never>((_, reject) => {
      setTimeout(() => {
        reject(new Error(`Request timeout after ${timeoutMs / 1000}s`))
      }, timeoutMs)
    })

    const response = await Promise.race([fetch(url.toString(), config), timeoutPromise])
    await this.handleResponse<{ success: boolean; message: string }>(response)
  }
}

export const contactService = new ContactService()
